import { Form, Formik, FormikHelpers } from 'formik'
import InputField from './InputField'
import { Box, Button, Collapse, Divider, Typography } from '@mui/material'
import { CredentialResponse, GoogleLogin } from '@react-oauth/google'
import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import { useAppTranslations } from '../../utils/hooks/useAppTranslations'
import { useLoginMutation, useLoginWithGoogleMutation } from '../../__generated__/graphql'
import { GOOGLE_LOGIN_ENABLED } from '../../env'
import { saveToken } from '../../utils/tokenManagement'
import { apolloClient } from '../../apollo'
import { resolvePostLoginPath } from '../../utils/loginRedirect'
import ErrorAlert from '../error/ErrorAlert'
import { StyledLink } from '../layout/styled'
import BackdropLoader from '../loader/BackdropLoader'
import { DivBottomLink } from '../layout/LoggedOutLayoutWrapper'
import { theme } from '../../styles/theme/theme'

const emailSchema = Yup.string().email('login.errors.invalidEmail')
const requiredEmailSchema = emailSchema.required('commons.required')

const buildLoginSchema = (showPasswordStep: boolean) =>
  Yup.object().shape({
    password: showPasswordStep ? Yup.string().required('commons.required') : Yup.string(),
    username: showPasswordStep ? requiredEmailSchema : emailSchema,
  })

interface LoginState {
  username: string
  password: string
  rememberMe: boolean
}

const LoginForm: React.FC = () => {
  const t = useAppTranslations()
  const navigate = useNavigate()
  const location = useLocation()
  const handledLoginTokenRef = React.useRef<string | null>(null)
  const [showPasswordStep, setShowPasswordStep] = React.useState(false)
  const [login, { loading: loginLoading, error: loginError, data: loginResult }] = useLoginMutation()
  const [loginWithGoogle, { loading: googleLoading, error: googleError, data: googleResult }] =
    useLoginWithGoogleMutation()

  const validationSchema = React.useMemo(() => buildLoginSchema(showPasswordStep), [showPasswordStep])

  const handleSubmit = async (values: LoginState, formikHelpers: FormikHelpers<LoginState>): Promise<void> => {
    if (!showPasswordStep) {
      formikHelpers.setFieldTouched('username', true, true)
      try {
        await requiredEmailSchema.validate(values.username)
        setShowPasswordStep(true)
      } catch (error) {
        if (error instanceof Yup.ValidationError) formikHelpers.setFieldError('username', error.message)
      }

      formikHelpers.setSubmitting(false)
      return
    }

    await login({
      variables: {
        email: values.username,
        password: values.password,
        rememberMe: values.rememberMe,
      },
    })
  }

  const initialValues: LoginState = {
    username: '',
    password: '',
    rememberMe: true,
  }

  const loginToken = loginResult?.login?.token ?? googleResult?.loginWithGoogle?.token

  React.useEffect(() => {
    if (!loginToken || handledLoginTokenRef.current === loginToken) return

    handledLoginTokenRef.current = loginToken
    const redirectTo = resolvePostLoginPath(location.pathname)
    if (redirectTo) navigate(redirectTo, { replace: true })

    saveToken(loginToken)
    void apolloClient.resetStore()
  }, [loginToken, location.pathname, navigate])

  const authError = loginError ?? googleError
  const showBackdrop = loginLoading || googleLoading

  return (
    <>
      <Typography
        align="center"
        variant="h1"
        sx={{ marginBottom: theme.spacing(2), fontWeight: 500, fontSize: '3rem' }}
      >
        {t('login.title')}
      </Typography>
      <BackdropLoader open={showBackdrop} />
      {authError && <ErrorAlert message={t(`serverError.${authError.message}`)} />}
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
        {(formik) => (
          <>
            <Form>
              <InputField
                data-testid="emailField"
                variant="outlined"
                name="username"
                label="commons.email"
                type="email"
                autoFocus
                required={showPasswordStep}
                size="medium"
                fullWidth
                defaultHelperText=" "
                margin="dense"
              />
              <Collapse in={showPasswordStep} unmountOnExit>
                <div>
                  <InputField
                    variant="outlined"
                    data-testid="passwordField"
                    name="password"
                    label="commons.password"
                    type="password"
                    required
                    size="medium"
                    fullWidth
                    defaultHelperText=" "
                    margin="dense"
                  />
                  <DivBottomLink>
                    <StyledLink to="/request-reset-password">{t('login.form.passwordRecovery')}</StyledLink>
                  </DivBottomLink>

                  <Button
                    style={{ marginTop: theme.spacing(2) }}
                    className="submit"
                    type="submit"
                    fullWidth
                    size="large"
                    variant="contained"
                    color="primary"
                    disabled={!formik.values.password || !formik.isValid}
                  >
                    {t('commons.login')}
                  </Button>
                </div>
              </Collapse>
              <Collapse in={!showPasswordStep} unmountOnExit>
                <div>
                  <Button
                    className="submit"
                    type="submit"
                    fullWidth
                    size="large"
                    variant="contained"
                    color="primary"
                    disabled={!formik.values.username}
                  >
                    {t('login.form.continue')}
                  </Button>
                </div>
              </Collapse>
            </Form>
            {GOOGLE_LOGIN_ENABLED && (
              <Collapse in={!showPasswordStep} unmountOnExit>
                <div>
                  <Divider
                    sx={(theme) => ({
                      my: 4,
                      '& .MuiDivider-wrapper': { fontSize: theme.typography.caption.fontSize },
                    })}
                  >
                    {t('login.form.orSeparator')}
                  </Divider>
                  <Box
                    sx={{
                      '& .google-login-container [role="button"], & .google-login-container [role="button"] span': {
                        fontSize: `${theme.typography.body1.fontSize} !important`,
                      },
                    }}
                  >
                    <GoogleLogin
                      size="large"
                      width="340"
                      containerProps={{ className: 'google-login-container' }}
                      onSuccess={async (credentialResponse: CredentialResponse) => {
                        if (!credentialResponse.credential) return

                        await loginWithGoogle({
                          variables: {
                            idToken: credentialResponse.credential,
                            rememberMe: formik.values.rememberMe,
                          },
                        })
                      }}
                      onError={() => undefined}
                      useOneTap={false}
                    />
                  </Box>
                </div>
              </Collapse>
            )}
          </>
        )}
      </Formik>
    </>
  )
}

export default LoginForm
